/**
 * PaymentMethodsSettings — Settings > Payment Methods (admin)
 *
 * Manages the list behind the transaction "Description" dropdown
 * (ACH / Wire / CC / ...). The list is stored in system settings and read by
 * the transaction dialog. "Value" is what's stored on the transaction (and used
 * to name the auto-generated reference, e.g. a Wire → "Wire-1a2b3c"); "Label"
 * is what shows in the dropdown.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { tokenStorage } from '../../lib/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${tokenStorage.getToken()}` } });

const PaymentMethodsSettings = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/settings/payment-methods`, authHeaders())
      .then((r) => setMethods(Array.isArray(r.data?.methods) ? r.data.methods : []))
      .catch(() => toast.error('Could not load payment methods'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (i, field, val) =>
    setMethods((m) => m.map((row, idx) => (idx === i ? { ...row, [field]: val } : row)));
  const addRow = () => setMethods((m) => [...m, { value: '', label: '' }]);
  const removeRow = (i) => setMethods((m) => m.filter((_, idx) => idx !== i));
  const move = (i, dir) =>
    setMethods((m) => {
      const j = i + dir;
      if (j < 0 || j >= m.length) return m;
      const copy = [...m];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const save = () => {
    const clean = methods
      .map((r) => ({
        value: (r.value || '').trim(),
        label: (r.label || '').trim() || (r.value || '').trim(),
      }))
      .filter((r) => r.value);
    if (!clean.length) {
      toast.error('Add at least one payment method');
      return;
    }
    setSaving(true);
    axios
      .put(`${API}/settings/payment-methods`, { methods: clean }, authHeaders())
      .then((r) => {
        setMethods(r.data?.methods || clean);
        toast.success('Payment methods saved');
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const reset = () => {
    if (!window.confirm('Restore the built-in payment method list?')) return;
    setSaving(true);
    axios
      .post(`${API}/settings/payment-methods/reset`, {}, authHeaders())
      .then((r) => {
        setMethods(r.data?.methods || []);
        toast.success('Reset to defaults');
      })
      .catch(() => toast.error('Reset failed'))
      .finally(() => setSaving(false));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Methods</CardTitle>
        <CardDescription>
          These are the options in the transaction “Description” dropdown. The <b>label</b> shows
          in the list; the <b>value</b> is stored on the transaction and used to name the reference
          for non-ACH payments (e.g. a Wire becomes “Wire-1a2b3c”).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-slate-500 px-1">
              <span>Value (stored)</span>
              <span>Label (shown)</span>
              <span />
            </div>
            {methods.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <Input value={m.value || ''} onChange={(e) => update(i, 'value', e.target.value)} placeholder="Wire" />
                <Input value={m.label || ''} onChange={(e) => update(i, 'label', e.target.value)} placeholder="Wire" />
                <div className="flex items-center gap-1">
                  <Button type="button" variant="ghost" size="icon" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => move(i, 1)} disabled={i === methods.length - 1} title="Move down">
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="text-red-600" onClick={() => removeRow(i)} title="Remove">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {methods.length === 0 && (
              <p className="text-sm text-slate-400">No methods — add at least one.</p>
            )}
            <div className="flex items-center justify-between pt-2">
              <Button type="button" variant="outline" size="sm" onClick={addRow}>
                <Plus className="h-4 w-4 mr-1" /> Add method
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={saving}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Reset
                </Button>
                <Button type="button" size="sm" onClick={save} disabled={saving}>
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentMethodsSettings;
